import { FC, useContext, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  ContextMenu,
  Label,
  Table,
  useContextMenu,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Column } from 'react-table';

import { AuthContext } from 'auth';
import { LayoutContext } from 'components/Layout';
import { LocalizationContext } from 'localization';
import { GroupModal } from 'modals';
import { TUserGroupsResponse, USER_GROUPS } from 'schemas';

import s from '../Clients.module.scss';
import { UserGroup } from '../interfaces';
import { TGroupsTableProps } from './interfaces';
import { useContextMenuItems } from './useContextMenuItems';

export const GroupsTable: FC<TGroupsTableProps> = ({ OpenTabRadio }) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const COLUMNS: Column<UserGroup>[] = [
    {
      Header: t('title'),
      accessor: 'title',
      maxWidth: 218,
    },
    {
      Header: t('user.participants'),
      accessor: 'user_count',
      Cell: ({ value }) => `${value} ${t('contracted.persons')}`,
      maxWidth: 142,
    },
    {
      Header: t('discount'),
      accessor: 'discount',
      Cell: ({ value }) => <Label text={`${value}%`} color="blue" rounded />,
      maxWidth: 114,
    },
    {
      Header: t('createdAt'),
      accessor: 'created_at',
      Cell: ({ value }) => (
        <span className={s.secondary}>
          {dayjs(value).format('D MMM YYYY HH:mm')}
        </span>
      ),
      width: 1142,
    },
  ];

  const [modalGroup, setModalGroup] = useState<
    UserGroup | Record<string, never> | null
  >(null);

  const { data: userGroupsData, loading } = useQuery<TUserGroupsResponse>(
    USER_GROUPS,
    {
      fetchPolicy: 'cache-and-network',
      skip: !permissions?.has('client_browse'),
    }
  );

  const data = userGroupsData?.userGroups || [];

  const layoutElement = useContext(LayoutContext);
  const contextMenuItems = useContextMenuItems(setModalGroup);
  const {
    contextMenuData,
    closeContextMenu,
    cursorPosition,
    onContextMenu,
    onTouchEnd,
    onTouchMove,
    onTouchStart,
  } = useContextMenu<UserGroup>();

  return (
    <>
      {permissions?.has('client_manage') &&
        contextMenuData &&
        cursorPosition && (
          <ContextMenu
            layoutElement={layoutElement}
            data={contextMenuData}
            items={contextMenuItems}
            closeContextMenu={closeContextMenu}
            position={cursorPosition}
          />
        )}
      {modalGroup && (
        <GroupModal
          group={modalGroup}
          onClose={() => {
            setModalGroup(null);
          }}
        />
      )}
      <div className={s.top}>
        <div className={s.radioWrapper}>{OpenTabRadio}</div>
        {permissions?.has('client_manage') && (
          <Button
            iconName="CirclePlusIcon"
            onClick={() => setModalGroup({})}
            disabled={!permissions?.has('client_manage')}
            className={s.button}
          >
            {t('group.create')}
          </Button>
        )}
      </div>
      <div className={s.table}>
        <Table
          data={data}
          columns={COLUMNS}
          isLoading={loading}
          sortBy={[{ id: 'created_at', desc: true }]}
          onRowDoubleClick={(_, group) => {
            if (!permissions?.has('client_manage')) return;
            setModalGroup(group);
          }}
          onContextMenu={onContextMenu}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className={s.table}
        />
      </div>
    </>
  );
};
